(() => {
  const ROOT_SELECTOR = '[data-sales-promotions-carousel]';
  const INITIALIZED = 'data-sales-promotions-carousel-initialized';

  class SalesPromotionsCarousel {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));
      this.previousButton = root.querySelector('[data-carousel-prev]');
      this.nextButton = root.querySelector('[data-carousel-next]');
      this.toggleButton = root.querySelector('[data-carousel-toggle]');
      this.pauseIcon = root.querySelector('.sales-promotions-carousel__pause-icon');
      this.playIcon = root.querySelector('.sales-promotions-carousel__play-icon');
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.timer = null;
      this.paused = false;
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.interval = Math.max(3000, (Number(root.dataset.autoplaySeconds) || 5) * 1000);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.onPrevious = () => this.show(this.currentIndex - 1, { restart: true });
      this.onNext = () => this.show(this.currentIndex + 1, { restart: true });
      this.onToggle = () => this.togglePlayback();
      this.onVisibilityChange = () => {
        if (document.hidden) this.stopTimer();
        else this.startTimer();
      };
      this.onPromoFilterClick = (event) => this.handlePromoFilterClick(event);

      this.bind();
      this.updateControls();
      this.startTimer();
    }

    bind() {
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.toggleButton?.addEventListener('click', this.onToggle);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = Number(dot.dataset.carouselDot);
          this.show(index, { restart: true });
        });
      });

      this.root.addEventListener('click', this.onPromoFilterClick);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    normalizeIndex(index) {
      if (!this.slides.length) return 0;
      if (index < 0) return this.slides.length - 1;
      if (index >= this.slides.length) return 0;
      return index;
    }

    show(index, { restart = false } = {}) {
      if (this.slides.length < 2) return;

      const nextIndex = this.normalizeIndex(index);
      this.currentIndex = nextIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === nextIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (restart) {
        this.stopTimer();
        this.startTimer();
      }
    }

    startTimer() {
      if (
        this.timer ||
        this.paused ||
        !this.autoplayEnabled ||
        this.reduceMotion ||
        this.slides.length < 2 ||
        document.hidden
      ) {
        return;
      }

      this.timer = window.setInterval(() => {
        this.show(this.currentIndex + 1);
      }, this.interval);
    }

    stopTimer() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    togglePlayback() {
      this.paused = !this.paused;

      if (this.paused) {
        this.stopTimer();
      } else {
        this.startTimer();
      }

      this.updateControls();
    }

    updateControls() {
      if (!this.toggleButton) return;

      this.toggleButton.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
      this.toggleButton.setAttribute('aria-label', this.paused ? 'Play carousel' : 'Pause carousel');

      if (this.pauseIcon) this.pauseIcon.hidden = this.paused;
      if (this.playIcon) this.playIcon.hidden = !this.paused;
    }

    async handlePromoFilterClick(event) {
      const link = event.target.closest('[data-sales-promo-filter]');
      if (!link || !this.root.contains(link)) return;

      const filterValue = String(link.dataset.filterValue || '').trim();
      if (!filterValue) return;

      event.preventDefault();

      if (window.BODiSalesPromotionsGrid?.applyFilter) {
        const applied = await window.BODiSalesPromotionsGrid.applyFilter(filterValue, {
          scroll: true,
        });

        if (applied) return;
      }

      // The Liquid href is built from the exact selected metaobject Product tag,
      // so it is also a safe progressive-enhancement fallback.
      window.location.assign(link.href);
    }
  }

  function init(root) {
    if (!root || root.hasAttribute(INITIALIZED)) return;
    root.setAttribute(INITIALIZED, 'true');
    new SalesPromotionsCarousel(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-carousel-slide]');
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (!slide || !root) return;

    const slides = Array.from(root.querySelectorAll('[data-carousel-slide]'));
    const index = slides.indexOf(slide);
    if (index < 0) return;

    slides.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    root.querySelectorAll('[data-carousel-dot]').forEach((dot, dotIndex) => {
      const active = dotIndex === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  });
})();
